import { isEmail } from '@side/pantry';
import * as yup from 'yup';

// Single source of required-ness for the sidebar badge + documents lockdown
// (via `computeMissingContactFields`).
const requiredString = () =>
  yup
    .string()
    .nullable()
    .transform((v) => (v?.trim() === '' ? null : v))
    .required('Required');

const emailFormat = <T extends yup.StringSchema<string | null | undefined>>(
  schema: T,
) => schema.test('email', 'Invalid Email', (v) => !v?.trim() || isEmail(v));

const optionalEmail = () => emailFormat(yup.string().nullable());
const requiredEmail = () => emailFormat(requiredString());

const titleOtherWhenOther = () =>
  yup
    .string()
    .nullable()
    .when('title', (titles, ts) =>
      titles[0] === 'other' ? ts.required('Required') : ts,
    );

export interface NameRequired {
  firstName: boolean;
  lastName: boolean;
  email: boolean;
}

// Brokerage flags default to not-required (only the offer seller's agent
// requires them today).
export interface AgentRequired extends NameRequired {
  licenseNumber?: boolean;
  brokerageName?: boolean;
  brokerageLicenseNumber?: boolean;
}

// The power-of-attorney signer's email defaults to required on the own-side
// client only; some configs also require it on the counterparty client.
export interface ClientRequired extends NameRequired {
  poaEmailRequired?: boolean;
}

const nameFields = (required: NameRequired) => ({
  firstName: required.firstName ? requiredString() : yup.string().nullable(),
  lastName: required.lastName ? requiredString() : yup.string().nullable(),
  email: required.email ? requiredEmail() : optionalEmail(),
});

// Trust / entity / probate / POA blocks: required only once their entityType is
// selected. `title` stays optional while the name/`titleOther` fields become
// required, matching the cover sheet.
const entityVariantShape = (poaEmailRequired: boolean) => ({
  trust: yup
    .object({
      trustName: yup.string().nullable(),
      title: yup.string().nullable(),
      titleOther: yup.string().nullable(),
    })
    .when('entityType', (values, schema) =>
      values[0] === 'trust'
        ? schema.shape({
            trustName: requiredString(),
            title: yup.string().nullable(),
            titleOther: titleOtherWhenOther(),
          })
        : schema,
    ),
  entity: yup
    .object({
      entityName: yup.string().nullable(),
      title: yup.string().nullable(),
      titleOther: yup.string().nullable(),
    })
    .when('entityType', (values, schema) =>
      values[0] === 'entity'
        ? schema.shape({
            entityName: requiredString(),
            title: yup.string().nullable(),
            titleOther: titleOtherWhenOther(),
          })
        : schema,
    ),
  probate: yup
    .object({
      estateName: yup.string().nullable(),
      title: yup.string().nullable(),
      titleOther: yup.string().nullable(),
    })
    .when('entityType', (values, schema) =>
      values[0] === 'probate'
        ? schema.shape({
            estateName: requiredString(),
            title: yup.string().nullable(),
            titleOther: titleOtherWhenOther(),
          })
        : schema,
    ),
  powerOfAttorney: yup
    .object({
      firstName: yup.string().nullable(),
      lastName: yup.string().nullable(),
      email: optionalEmail(),
    })
    .when('entityType', (values, schema) =>
      values[0] === 'powerOfAttorney'
        ? schema.shape({
            firstName: requiredString(),
            lastName: requiredString(),
            email: poaEmailRequired ? requiredEmail() : optionalEmail(),
          })
        : schema,
    ),
});

const clientObject = (required: NameRequired, poaEmailRequired: boolean) =>
  yup.object({
    entityType: yup.string().nullable(),
    ownershipStatus: yup.string().nullable(),
    ...nameFields(required),
    cellPhone: yup.string().nullable(),
    isSuccessor: yup.string().nullable(),
    ...entityVariantShape(poaEmailRequired),
  });

const agentObject = (required: AgentRequired) =>
  yup.object({
    ...nameFields(required),
    cellPhone: yup.string().nullable(),
    licenseNumber: required.licenseNumber
      ? requiredString()
      : yup.string().nullable(),
    companyName: yup.string().nullable(),
    mlsId: yup.string().nullable(),
    primaryMls: yup.string().nullable(),
    brokerage: yup.object({
      companyName: required.brokerageName
        ? requiredString()
        : yup.string().nullable(),
      licenseNumber: required.brokerageLicenseNumber
        ? requiredString()
        : yup.string().nullable(),
      mlsCode: yup.string().nullable(),
      mlsId: yup.string().nullable(),
      officeEmail: optionalEmail(),
      street1: yup.string().nullable(),
      street2: yup.string().nullable(),
      city: yup.string().nullable(),
      state: yup.string().nullable(),
      zip: yup.string().nullable(),
    }),
  });

const optionalContact = yup.object({
  firstName: yup.string().nullable(),
  lastName: yup.string().nullable(),
  email: optionalEmail(),
  cellPhone: yup.string().nullable(),
});

export interface ContactsRequiredFields {
  agent: AgentRequired;
  counterpartyAgent: AgentRequired;
  client: ClientRequired;
  counterpartyClient: ClientRequired;
}

export const buildContactsSchema = ({
  agent,
  counterpartyAgent,
  client,
  counterpartyClient,
}: ContactsRequiredFields) =>
  yup.object({
    contacts: yup.object({
      agent: yup.array().of(agentObject(agent)).default([]),
      client: yup
        .array()
        .of(clientObject(client, client.poaEmailRequired ?? true))
        .default([]),
      counterpartyAgent: yup
        .array()
        .of(agentObject(counterpartyAgent))
        .default([]),
      counterpartyClient: yup
        .array()
        .of(
          clientObject(
            counterpartyClient,
            counterpartyClient.poaEmailRequired ?? false,
          ),
        )
        .default([]),
      counterpartyTransactionCoordinator: yup
        .array()
        .of(optionalContact)
        .default([]),
      titleOfficer: yup.array().of(optionalContact).default([]),
      escrowOfficer: yup.array().of(optionalContact).default([]),
      lender: yup.array().of(optionalContact).default([]),
      appraiser: yup.array().of(optionalContact).default([]),
      attorney: yup.array().of(optionalContact).default([]),
      counterpartyAttorney: yup.array().of(optionalContact).default([]),
      closingAttorney: yup.array().of(optionalContact).default([]),
      propertyManagingAgent: yup.array().of(optionalContact).default([]),
      homeWarranty: yup.array().of(optionalContact).default([]),
      other: yup.array().of(optionalContact).default([]),
    }),
  });
